import { Component } from 'react';
import Scrollbars from 'react-custom-scrollbars-2';
import { connect } from 'react-redux';

import Icon from 'components/atoms/Icon';
import Input from 'components/atoms/Input';
import { bindActionCreators, Dispatch } from 'redux';
import { SetLayoutAction, setLayoutAction } from 'store/actions/layoutActions';
import { setUserAction } from 'store/actions/userActions';
import { LogEntry, OpenModal, RootState } from 'types';
import { OnChangeType } from 'types/functions';
import { DateTimeFormat } from 'utils/dateTimeFormat';
import { getId } from 'utils/formHandlers';
import { translate } from 'utils/translate';
import { twclsx } from 'utils/twclsx';

interface SystemConsoleComponentProps {
    active: boolean;
    setLayoutAction: SetLayoutAction;
    openModelFunction: OpenModal;
    dateTimeFormat: DateTimeFormat;
}

interface SystemConsoleComponentState {
    active: boolean;
    logs: LogEntry[];
    command: string; // Add a command field for Input
    overlay: boolean;
}

class SystemConsole extends Component<SystemConsoleComponentProps, SystemConsoleComponentState> {
    constructor(props: SystemConsoleComponentProps) {
        super(props);
        this.state = {
            active: false,
            logs: [],
            command: '',
            overlay: true,
        };
    }

    static getDerivedStateFromProps(props: SystemConsoleComponentProps, state: SystemConsoleComponentState) {
        if (props.active !== state.active) {
            return { active: props.active };
        }
        return null;
    }

    componentDidMount(): void {
        //@ts-ignore
        // eslint-disable-next-line prettier/prettier
        window?.log = this.consoleLog;
    }

    parseStyleString = (style: string): React.CSSProperties => {
        return style.split(';').reduce((acc: React.CSSProperties, rule) => {
            const [property, value] = rule.split(':').map((str) => str.trim());
            if (property && value) {
                // Convert property from kebab-case to camelCase
                const camelCaseProperty = property.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
                // Assign the value to the property
                //@ts-ignore
                acc[camelCaseProperty as keyof React.CSSProperties] = value;
            }
            return acc;
        }, {});
    };

    consoleLog = (message: string, style?: string) => {
        // Convert string styles into a CSSProperties object if needed
        const styleObj: React.CSSProperties | undefined = style ? this.parseStyleString(style) : undefined;

        this.setState((prevState) => ({
            logs: [
                { time: this.props.dateTimeFormat.parseDateTime(), id: getId(), message, style: styleObj },
                ...prevState.logs,
            ],
        }));
    };

    handleCommandChange: OnChangeType = (name, value) => {
        this.setState({ command: value });
    };

    handleCommandSubmit = () => {
        if (this.state.command.trim()) {
            switch (this.state.command.trim().toUpperCase()) {
                case 'CLEAR':
                    this.setState({
                        logs: [],
                    });
                    break;

                default:
                    break;
            }

            this.consoleLog(`${this.state.command}`, 'color: blue;');
            this.setState({ command: '' });
        }
    };

    toggleOverlay = () => {
        this.setState({
            overlay: !this.state.overlay,
        });
    };

    render() {
        return (
            <>
                {/* Overlay */}
                <div
                    className={twclsx(
                        this.state.active && this.state.overlay ? 'block' : 'hidden',
                        'z-40 fixed left-0 top-0 h-screen w-screen bg-black bg-opacity-30'
                    )}
                ></div>

                {/* Console */}
                <div
                    className={twclsx(
                        this.state.active ? 'right-0' : 'system-right-out',
                        'flex flex-col z-40 fixed w-console h-screen top-0 bg-white shadow-xl position-transition'
                    )}
                >
                    {/* Header */}
                    <div className="flex justify-between p-4">
                        <div className="overflow-hidden text-lg">{translate('System console')}</div>

                        <div className="flex justify-between gap-2">
                            <div onClick={this.toggleOverlay} className="cursor-pointer">
                                <Icon>
                                    <path d="M21 12v3a1 1 0 0 1 -1 1h-16a1 1 0 0 1 -1 -1v-10a1 1 0 0 1 1 -1h9"></path>
                                    <path d="M7 20l10 0"></path>
                                    <path d="M9 16l0 4"></path>
                                    <path d="M15 16l0 4"></path>
                                    <path d="M17 4h4v4"></path>
                                    <path d="M16 9l5 -5"></path>
                                </Icon>
                            </div>
                            <div
                                onClick={() => {
                                    this.props.setLayoutAction([
                                        {
                                            action: 'UPDATE_MENU',
                                            data: {
                                                name: 'systemConsole',
                                                section: 'system',
                                                state: false,
                                            },
                                        },
                                    ]);
                                }}
                                className="cursor-pointer"
                            >
                                <Icon>
                                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                    <path d="M18 6l-12 12" />
                                    <path d="M6 6l12 12" />
                                </Icon>
                            </div>
                        </div>
                    </div>

                    {/* Logs */}
                    <div className="pl-4 pr-4 w-full h-console">
                        <Scrollbars
                            renderThumbHorizontal={() => <div style={{ display: 'none' }} />}
                            renderTrackHorizontal={() => <div style={{ display: 'none' }} />}
                            autoHide
                            className="h-full"
                        >
                            {this.state.logs.map((log) => (
                                <div className="text-sm flex gap-4 justify-start" key={log.id}>
                                    <div className="w-32">
                                        {log.time.formatDateTimeToString('dd MMM yyyy HH:mm:ss')}
                                    </div>
                                    <div style={log.style || {}}>{log.message}</div>
                                </div>
                            ))}
                        </Scrollbars>
                    </div>

                    {/* Input */}
                    <div className="pl-4 pr-4 w-full mt-4">
                        <Input
                            name="command"
                            className="w-full"
                            value={this.state.command}
                            onChange={this.handleCommandChange}
                            onKeyPress={(name, key) => key === 'Enter' && this.handleCommandSubmit()}
                            placeholder="Type a command..."
                        />
                    </div>
                </div>
            </>
        );
    }
}

const systemConsoleStateToProps = (state: RootState) => ({
    openModelFunction: state.layout.openModelFunction,
    dateTimeFormat: state.port.dateTimeFormat,
});
const systemConsoleDispatchToProps = (dispatch: Dispatch) => ({
    setUserIdAction: bindActionCreators(setUserAction, dispatch),
    setLayoutAction: bindActionCreators(setLayoutAction, dispatch),
});

export default connect(systemConsoleStateToProps, systemConsoleDispatchToProps)(SystemConsole);
